import { useState } from 'react';
import { SectionTitle } from '../../../components/sectionTitle';
import { RegistrationFormData, APPAREL_SIZES } from '../../../types/Registration';
import { EventItem, EventOption } from '../../../types/Events';

export interface IRejestracjaFormProps {
    formData: RegistrationFormData;
    formVisible: boolean;
    submitted: boolean;
    activeEvents: EventItem[];
    selectedOptions: Record<string, number[]>;
    eventOptionsMap: Record<string, {
        transport: EventOption[];
        hotel: EventOption[];
    }>;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onEventChange: (eventId: string) => void;
    onOptionChange: (eventId: string, optionId: number, optionType?: 'transport' | 'hotel') => void;
    onSizeChange: (field: 'shirtSize' | 'jerseySize', size: string) => void;
    onSubmit: (e: React.FormEvent) => Promise<void>;
    onToggle: () => void;
}

// Stała dla opcji rezygnacji
const REZYGNUJE = 'REZYGNUJE';

export const RejestracjaForm = (props: IRejestracjaFormProps) => {
    const {
        formData,
        formVisible,
        submitted,
        activeEvents,
        selectedOptions,
        eventOptionsMap,
        onInputChange,
        onEventChange,
        onOptionChange,
        onSizeChange,
        onSubmit,
        onToggle
    } = props;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Funkcja sprawdzająca czy wszystkie wybrane wydarzenia mają wybrane opcje
    const getMissingOptions = (): { missingTransport: boolean; missingHotel: boolean; hasAnyOptions: boolean } => {
        if (formData.selectedEvents.length === 0) {
            return { missingTransport: false, missingHotel: false, hasAnyOptions: false };
        }

        const selectedEventId = formData.selectedEvents[0];
        const options = eventOptionsMap[selectedEventId];

        if (!options || (options.transport.length === 0 && options.hotel.length === 0)) {
            return { missingTransport: false, missingHotel: false, hasAnyOptions: false };
        }

        const selectedForEvent = selectedOptions[selectedEventId] || [];

        const hasTransport = options.transport.length > 0;
        const hasHotel = options.hotel.length > 0;

        const missingTransport = hasTransport && !selectedForEvent.some(id =>
            options.transport.some(opt => opt.id === id)
        );

        const missingHotel = hasHotel && !selectedForEvent.some(id =>
            options.hotel.some(opt => opt.id === id)
        );

        return {
            missingTransport,
            missingHotel,
            hasAnyOptions: hasTransport || hasHotel
        };
    };

    const getValidationMessage = (): string | null => {
        const { missingTransport, missingHotel, hasAnyOptions } = getMissingOptions();

        if (!hasAnyOptions) return null;

        if (missingTransport && missingHotel) {
            return 'Dla wybranego wydarzenia musisz wybrać zarówno opcję transportu, jak i noclegu.';
        }

        if (missingTransport) {
            return 'Dla wybranego wydarzenia musisz wybrać opcję transportu.';
        }

        if (missingHotel) {
            return 'Dla wybranego wydarzenia musisz wybrać opcję noclegu.';
        }

        return null;
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting) {
            return;
        }

        setSubmitError(null);

        if (formData.selectedEvents.length === 0) {
            setSubmitError('Wybierz jedno wydarzenie.');
            return;
        }

        if (formData.selectedEvents.length > 1) {
            setSubmitError('Możesz wybrać tylko jedno wydarzenie.');
            return;
        }

        const validationMessage = getValidationMessage();
        if (validationMessage) {
            setSubmitError(validationMessage);
            return;
        }

        const selectedEventId = formData.selectedEvents[0];
        const selectedEvent = activeEvents.find(e => e.id === selectedEventId);
        const offersShirt = selectedEvent?.offers_shirt !== false;
        const offersJersey = selectedEvent?.offers_jersey !== false;

        if (offersShirt && !formData.shirtSize) {
            setSubmitError('Wybierz rozmiar koszulki.');
            return;
        }

        if (offersJersey && !formData.jerseySize) {
            setSubmitError('Wybierz rozmiar Jersey lub opcję "Rezygnuję".');
            return;
        }

        const nameParts = formData.name.trim().split(/\s+/);
        if (nameParts.length < 2) {
            setSubmitError('Podaj imię i nazwisko (minimum dwa wyrazy).');
            return;
        }

        const phoneRegex = /^[0-9+\s\-()]+$/;
        if (formData.contact && !phoneRegex.test(formData.contact)) {
            setSubmitError('Numer telefonu może zawierać tylko cyfry, spacje, myślniki, nawiasy i znak plus.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            setSubmitError('Podaj poprawny adres e-mail (np. jan@example.com).');
            return;
        }

        setIsSubmitting(true);

        try {
            await onSubmit(e);
        } catch (error) {
            let errorMessage = 'Wystąpił błąd podczas wysyłania zgłoszenia. Spróbuj ponownie.';

            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            if (errorMessage.includes('już zapisany') || errorMessage.includes('zapisany')) {
                errorMessage = '⚠️ ' + errorMessage;
            }

            setSubmitError(errorMessage);
            setIsSubmitting(false);
        }
    };

    const isOptionSelected = (eventId: string, optionId: number): boolean => {
        return (selectedOptions[eventId] || []).includes(optionId);
    };

    const isOptionFull = (option: EventOption): boolean => {
        if (option.has_capacity_limit === false) return false;
        return option.current_count >= option.max_capacity;
    };

    // Ten sam tekst pomocniczy co w index.tsx — opcja bez limitu nie pokazuje
    // "x/y miejsc" (bo max_capacity jest tam tylko wartością domyślną, a nie
    // realnym ograniczeniem), tylko sam licznik wyborów.
    const formatOptionCapacity = (opt: EventOption): string => {
        if (opt.has_capacity_limit === false) {
            return `${opt.current_count} wybrało · bez limitu miejsc`;
        }
        return `${opt.current_count}/${opt.max_capacity} miejsc`;
    };

    const hasTransportSelected = (eventId: string): boolean => {
        const options = eventOptionsMap[eventId];
        if (!options || options.transport.length === 0) return true;
        const selectedForEvent = selectedOptions[eventId] || [];
        return selectedForEvent.some(id =>
            options.transport.some(opt => opt.id === id)
        );
    };

    const hasHotelSelected = (eventId: string): boolean => {
        const options = eventOptionsMap[eventId];
        if (!options || options.hotel.length === 0) return true;
        const selectedForEvent = selectedOptions[eventId] || [];
        return selectedForEvent.some(id =>
            options.hotel.some(opt => opt.id === id)
        );
    };

    // Komponent do wyboru rozmiaru
    const SizeSelector = ({
        label,
        value,
        onChange,
        required = false,
        showRezygnuje = false,
        rezygnujeLabel = 'Rezygnuję'
    }: {
        label: string;
        value: string;
        onChange: (val: string) => void;
        required?: boolean;
        showRezygnuje?: boolean;
        rezygnujeLabel?: string;
    }) => {
        const isRezygnuje = value === REZYGNUJE;
        const selectedSize = !isRezygnuje ? value : '';

        return (
            <div className="rejestracja__field">
                <label className="rejestracja__label">
                    {label} {required && '*'}
                </label>
                <div className="rejestracja__size-group">
                    {APPAREL_SIZES.map((size) => (
                        <button
                            key={size}
                            type="button"
                            className={`rejestracja__size-btn${selectedSize === size ? ' rejestracja__size-btn--selected' : ''}`}
                            onClick={() => onChange(size)}
                            disabled={isSubmitting}
                        >
                            {size}
                        </button>
                    ))}
                    {showRezygnuje && (
                        <button
                            type="button"
                            className={`rejestracja__size-btn rejestracja__size-btn--rezygnuje${isRezygnuje ? ' rejestracja__size-btn--selected' : ''}`}
                            onClick={() => onChange(REZYGNUJE)}
                            disabled={isSubmitting}
                        >
                            ✕ {rezygnujeLabel}
                        </button>
                    )}
                </div>
                {showRezygnuje && (
                    <p className="rejestracja__size-note">
                        Wybierz "Rezygnuję" jeśli nie chcesz zamawiać jersey.
                    </p>
                )}
                {!showRezygnuje && (
                    <p className="rejestracja__size-note">
                        Zapotrzebowanie na dodatkowe koszulki zgłoś w komentarzu.
                    </p>
                )}
            </div>
        );
    };

    if (submitted) {
        return (
            <div className="confirmation__box">
                <div className="confirmation__message confirmation__success">
                    <h2>✅ Zgłoszenie wysłane</h2>
                    <p>Twoje zgłoszenie zostało zapisane.</p>
                    <p>Wkrótce się z Tobą skontaktujemy.</p>
                    <p className='text-weight--bold text-color--primary'>Proszę sprawdzić pocztę elektroniczną i potwierdzić poprawność adresu e-mail.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="rejestracja__form-container">
            <h2 className="rejestracja__form-heading">ZAPISY NA WYDARZENIE</h2>
            <p className="rejestracja__form-intro">
                Wybierz jedno wydarzenie, w którym chcesz wziąć udział.
            </p>

            <div className="rejestracja__toggle-wrapper">
                <div className="custom-button__link-wrapper">
                    <button
                        type="button"
                        className="custom-button"
                        onClick={onToggle}
                        disabled={isSubmitting}
                    >
                        {formVisible ? 'Ukryj formularz' : 'Pokaż formularz rejestracyjny'}
                    </button>
                </div>
            </div>

            {formVisible && (
                <form className="rejestracja__form" onSubmit={handleFormSubmit}>
                    <div className="rejestracja__field">
                        <label className="rejestracja__label">
                            Imię i nazwisko *
                        </label>
                        <input
                            className="rejestracja__input"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={onInputChange}
                            required
                            disabled={isSubmitting}
                            title="Podaj imię i nazwisko (minimum dwa wyrazy)"
                        />
                    </div>

                    <div className="rejestracja__field">
                        <label className="rejestracja__label">Adres e-mail *</label>
                        <input
                            className="rejestracja__input"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={onInputChange}
                            required
                            disabled={isSubmitting}
                            pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                            title="Podaj poprawny adres e-mail (np. jan@example.com)"
                        />
                    </div>

                    <div className="rejestracja__field">
                        <label className="rejestracja__label">Numer telefonu *</label>
                        <input
                            className="rejestracja__input"
                            type="tel"
                            name="contact"
                            placeholder="np. 123456789 lub +48 123 456 789"
                            value={formData.contact}
                            onChange={onInputChange}
                            required
                            disabled={isSubmitting}
                            pattern="[0-9+\s\-()]+"
                            title="Numer telefonu może zawierać tylko cyfry, spacje, myślniki, nawiasy i znak plus"
                        />
                    </div>

                    <div className="rejestracja__field">
                        <label className="rejestracja__label">Marka motocykla *</label>
                        <input
                            className="rejestracja__input"
                            type="text"
                            name="motorcycleBrand"
                            placeholder="np. Honda, Yamaha, BMW"
                            value={formData.motorcycleBrand}
                            onChange={onInputChange}
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="rejestracja__field">
                        <label className="rejestracja__label">
                            Ekipa / ksywa *
                        </label>
                        <input
                            className="rejestracja__input"
                            type="text"
                            name="nickname"
                            value={formData.nickname}
                            onChange={onInputChange}
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="rejestracja__field">
                        <label className="rejestracja__label">Typ zgłoszenia</label>
                        <div className="rejestracja__radio-group">
                            <label className="rejestracja__radio-label">
                                <input
                                    type="radio"
                                    name="registrationType"
                                    value="individual"
                                    checked={formData.registrationType === 'individual'}
                                    onChange={onInputChange}
                                    disabled={isSubmitting}
                                />
                                Motocyklista solo
                            </label>
                            <label className="rejestracja__radio-label">
                                <input
                                    type="radio"
                                    name="registrationType"
                                    value="group"
                                    checked={formData.registrationType === 'group'}
                                    onChange={onInputChange}
                                    disabled={isSubmitting}
                                />
                                Motocyklista z rodziną
                            </label>
                        </div>
                    </div>

                    {formData.registrationType === 'group' && (
                        <div className="rejestracja__field">
                            <label className="rejestracja__label">
                                Liczba uczestników *
                            </label>
                            <input
                                className="rejestracja__input rejestracja__input--number"
                                type="number"
                                name="groupSize"
                                min="2"
                                value={formData.groupSize}
                                onChange={onInputChange}
                                disabled={isSubmitting}
                            />
                        </div>
                    )}

                    {/* Szczegółowy wybór wydarzenia z opcjami */}
                    <div className="rejestracja__field">
                        <h3 className="rejestracja__events-heading">Wybierz wydarzenie i opcje *</h3>
                        {activeEvents.length === 0 ? (
                            <p className="rejestracja__no-events">
                                Brak dostępnych wydarzeń. Wróć wkrótce.
                            </p>
                        ) : (
                            activeEvents.map((event) => {
                                const isSelected = formData.selectedEvents.includes(event.id);
                                const isClosed = !event.registrations_open;

                                const hasNewOptions = eventOptionsMap[event.id] &&
                                    (eventOptionsMap[event.id].transport.length > 0 ||
                                     eventOptionsMap[event.id].hotel.length > 0);

                                const hasOldOptions = event.options && event.options.length > 0;

                                const transportOk = hasTransportSelected(event.id);
                                const hotelOk = hasHotelSelected(event.id);
                                const optionsOk = transportOk && hotelOk;

                                return (
                                    <div key={event.id} className="rejestracja__event-select-block">
                                        <label
                                            className={`rejestracja__event-item-extra rejestracja__event-item${isClosed ? ' rejestracja__event-item--disabled' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="selectedEvent"
                                                checked={isSelected}
                                                disabled={isClosed || isSubmitting}
                                                onChange={() => onEventChange(event.id)}
                                            />
                                            <div>
                                                <strong>{event.title}</strong>
                                                {event.date && (
                                                    <div className="rejestracja__event-date">
                                                        {event.date}
                                                    </div>
                                                )}
                                                {isClosed && (
                                                    <div className="rejestracja__event-closed-note">
                                                        Zapisy na to wydarzenie są obecnie niedostępne
                                                    </div>
                                                )}
                                            </div>
                                        </label>

                                        {isSelected && hasNewOptions && (
                                            <div className="rejestracja__event-options">
                                                <span className="rejestracja__event-options-label">
                                                    Wybierz opcje dla: {event.title} *
                                                </span>

                                                {!optionsOk && (
                                                    <div className="rejestracja__event-options-warning">
                                                        ⚠️ {!transportOk && !hotelOk && 'Musisz wybrać zarówno transport, jak i nocleg'}
                                                        {!transportOk && hotelOk && 'Musisz wybrać opcję transportu'}
                                                        {transportOk && !hotelOk && 'Musisz wybrać opcję noclegu'}
                                                    </div>
                                                )}

                                                {eventOptionsMap[event.id].transport.length > 0 && (
                                                    <div className="rejestracja__event-options-group">
                                                        <span className="rejestracja__event-options-group-title">
                                                            🚌 Transport (wybierz jeden) *
                                                        </span>
                                                        {eventOptionsMap[event.id].transport.map((opt) => {
                                                            const isSelectedOption = isOptionSelected(event.id, opt.id);
                                                            const isFull = isOptionFull(opt);

                                                            return (
                                                                <label
                                                                    key={opt.id}
                                                                    className={`rejestracja__event-option-item${isFull ? ' rejestracja__event-option-item--disabled' : ''}`}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name={`transport-${event.id}`}
                                                                        checked={isSelectedOption}
                                                                        onChange={() => {
                                                                            if (!isFull && !isSubmitting) {
                                                                                onOptionChange(event.id, opt.id, 'transport');
                                                                            }
                                                                        }}
                                                                        disabled={isFull || isSubmitting}
                                                                    />
                                                                    <span>
                                                                        {opt.option_name}
                                                                        <span className="rejestracja__event-option-availability">
                                                                            ({formatOptionCapacity(opt)})
                                                                        </span>
                                                                        {isFull && <span className="rejestracja__event-option-full">— BRAK MIEJSC</span>}
                                                                    </span>
                                                                </label>
                                                            );
                                                        })}
                                                        <p className="rejestracja__size-note">
                                                            Jeden motocykl na zgłoszenie.
                                                        </p>
                                                    </div>
                                                )}

                                                {eventOptionsMap[event.id].hotel.length > 0 && (
                                                    <div className="rejestracja__event-options-group">
                                                        <span className="rejestracja__event-options-group-title">
                                                            🏨 Nocleg (wybierz jeden) *
                                                        </span>
                                                        {eventOptionsMap[event.id].hotel.map((opt) => {
                                                            const isSelectedOption = isOptionSelected(event.id, opt.id);
                                                            const isFull = isOptionFull(opt);

                                                            return (
                                                                <label
                                                                    key={opt.id}
                                                                    className={`rejestracja__event-option-item${isFull ? ' rejestracja__event-option-item--disabled' : ''}`}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name={`hotel-${event.id}`}
                                                                        checked={isSelectedOption}
                                                                        onChange={() => {
                                                                            if (!isFull && !isSubmitting) {
                                                                                onOptionChange(event.id, opt.id, 'hotel');
                                                                            }
                                                                        }}
                                                                        disabled={isFull || isSubmitting}
                                                                    />
                                                                    <span>
                                                                        {opt.option_name}
                                                                        <span className="rejestracja__event-option-availability">
                                                                            ({formatOptionCapacity(opt)})
                                                                        </span>
                                                                        {isFull && <span className="rejestracja__event-option-full">— BRAK MIEJSC</span>}
                                                                    </span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {isSelected && hasOldOptions && !hasNewOptions && (
                                            <div className="rejestracja__event-options">
                                                <span className="rejestracja__event-options-label">
                                                    Wybierz opcje dla: {event.title}
                                                </span>
                                                {event.options!.map((option, i) => {
                                                    const optionId = `${event.id}-option-${i}`;
                                                    const isOptionSelected = (selectedOptions[event.id] || []).includes(option as any);
                                                    return (
                                                        <label key={optionId} className="rejestracja__event-option-item">
                                                            <input
                                                                type="checkbox"
                                                                checked={isOptionSelected}
                                                                onChange={() => onOptionChange(event.id, option as any)}
                                                                disabled={isSubmitting}
                                                            />
                                                            <span>{option}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Rozmiary odzieży - wyświetlane tylko jeśli wybrane wydarzenie je oferuje */}
                    {formData.selectedEvents.length > 0 && (
                        (() => {
                            const selectedEventId = formData.selectedEvents[0];
                            const selectedEvent = activeEvents.find(e => e.id === selectedEventId);
                            const offersShirt = selectedEvent?.offers_shirt !== false;
                            const offersJersey = selectedEvent?.offers_jersey !== false;

                            return (
                                <>
                                    {offersShirt && (
                                        <SizeSelector
                                            label="Rozmiar koszulki"
                                            value={formData.shirtSize}
                                            onChange={(size) => onSizeChange('shirtSize', size)}
                                            required={true}
                                            showRezygnuje={false}
                                        />
                                    )}

                                    {offersJersey && (
                                        <SizeSelector
                                            label="Rozmiar Jersey"
                                            value={formData.jerseySize}
                                            onChange={(size) => onSizeChange('jerseySize', size)}
                                            required={true}
                                            showRezygnuje={true}
                                            rezygnujeLabel="Rezygnuję z jersey"
                                        />
                                    )}

                                    {!offersShirt && !offersJersey && (
                                        <div className="rejestracja__info-message">
                                            <p>⚠️ To wydarzenie nie oferuje koszulek ani jersey.</p>
                                        </div>
                                    )}

                                    {offersShirt && offersJersey && (
                                        <p className="rejestracja__size-note rejestracja__size-note--info">
                                            Koszulka jest w cenie wydarzenia, Jersey jest dodatkowo płatny.
                                        </p>
                                    )}
                                </>
                            );
                        })()
                    )}

                    <div className="rejestracja__field">
                        <label className="rejestracja__label">Komentarz</label>
                        <textarea
                            className="rejestracja__textarea"
                            name="comment"
                            placeholder="Istnieje możliwość zamówienia dodatkowych opon, oraz innych akcesoriów spoza formularza ze specialnym rabatem dla członków ekipy HARDADV. Daj znać co potrzebujesz. Wpisz tutaj..."
                            rows={5}
                            value={formData.comment}
                            onChange={onInputChange}
                            disabled={isSubmitting}
                        />
                    </div>

                    {submitError && (
                        <div className={`rejestracja__error-message${submitError.includes('już zapisany') ? ' rejestracja__error-message--duplicate' : ''}`}>
                            <span className="rejestracja__error-icon">
                                {submitError.includes('już zapisany') ? '⚠️' : '❌'}
                            </span>
                            {submitError}
                        </div>
                    )}

                    <div className="rejestracja__submit-wrapper">
                        <div className="custom-button__link-wrapper custom-button__link-wrapper--primary">
                            <button
                                type="submit"
                                className={`custom-button${isSubmitting ? ' custom-button--loading' : ''}`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="custom-button__spinner"></span>
                                        Wysyłanie...
                                    </>
                                ) : (
                                    'Wyślij zgłoszenie'
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="content-wrapper rozklad-jazdy__content-wrapper">
                        <div className="rozklad-jazdy-section-2">
                            <SectionTitle title={<><span className="rejestracja__event-title3">INFORMACJE</span> <span className="rejestracja__event-title2">ORGANIZACYJNE</span></>} customClassName={'rejestracja__event-title2'} />
                            <p className='text-weight--bold'>Wypełniając formularz, potwierdzasz, że zapoznałeś się ze wszystkimi szczegółami wydarzenia.
                            Akceptujesz warunki i zgłaszasz chęć udziału w wyprawie.</p>
                            <p className='text-weight--bold'>*pola obowiązkowe</p>
                            <p className='text-weight--bold text-color--primary'>Adres E-mail wymaga potwierdzenia</p>
                            <p>Po wypełnieniu formularza otrzymasz wiadomość e-mail do potwierdzenia poprawności adresu, by później otrzymać status weryfikacji zapisu na wydarzenie.</p>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};
